import React, { useCallback, useMemo } from "react";
import type { CompositeScreenProps } from "@react-navigation/native";
import type { StackScreenProps } from "@react-navigation/stack";
import { useQuery } from "@apollo/client/react";
import { useNavigation } from "@react-navigation/native";
import { ProfileStackParamList } from "../../navigations/ProfileStack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useI18n } from "../../hooks/i18n-hooks";
import { FIND_MY_MISSIONS } from "../../graphql/home";
import { ProfileType } from "../../__generated__/graphql";
import { CoreMissionFieldsFragment } from "../../__generated__/operations/graphql";
import ScreenCard from "../ScreenCard";
import { useListGroupRenderItem } from "../../components/ListGroup";
import EmptyScreen from "../../components/EmptyScreen";
import Loading from "../../components/Loading";
import { useMissionDisplayAttributes } from "../../hooks/missions";
import { buildErrorHeadband } from "../ScreenCardHeadband";
import NoActivitiesImage from "../../assets/images/no-activities-image.svg";

type Props = CompositeScreenProps<
    StackScreenProps<ProfileStackParamList, "ColleagueContracts">,
    StackScreenProps<RootStackParamList>
>;

const ColleagueContractsScreen: React.FC<Props> = ({ route }) => {
    const { colleagueId } = route.params;
    const i18n = useI18n();
    const navigation = useNavigation<Props["navigation"]>();

    const {
        loading: missionsLoading,
        error: missionsError,
        data: missionsData,
    } = useQuery(FIND_MY_MISSIONS, {
        variables: {},
    });

    // Filter missions where colleague is responsible (clientInfo.id === colleague.id)
    const colleagueContracts = useMemo(() => {
        const allMissions = missionsData?.findMyMissions?.elements || [];
        return allMissions.filter(
            (mission: CoreMissionFieldsFragment) => mission.clientInfo?.id === colleagueId,
        );
    }, [missionsData?.findMyMissions?.elements, colleagueId]);

    const onSelectMission = useCallback(
        (missionId: string) => {
            navigation.navigate("MissionDetails", { missionId });
        },
        [navigation],
    );

    const getMissionDisplayAttributes = useMissionDisplayAttributes();

    const items = useMemo(() => {
        if (!colleagueContracts.length) {
            return [];
        }

        return colleagueContracts.map((mission: CoreMissionFieldsFragment) => {
            const displayAttributes = getMissionDisplayAttributes(mission, ProfileType.Client);
            const title = mission.workerInfo?.name || "";
            const description = `${mission.name || ""}\n\n${displayAttributes.durationInfo || ""}`;

            return {
                id: mission.id,
                value: mission.id,
                useBigAvatarOrIcon: true,
                title,
                description,
                avatarPath: mission.workerInfo?.avatarUrl || null,
                avatarEmail: mission.workerInfo?.email || null,
                onPress: () => onSelectMission(mission.id),
            };
        });
    }, [colleagueContracts, getMissionDisplayAttributes, onSelectMission]);

    const renderRow = useListGroupRenderItem(items);
    const showList = !missionsLoading && !missionsError && items.length > 0;

    return (
        <ScreenCard
            headband={
                missionsError
                    ? buildErrorHeadband(i18n.t("screens.user.missions.cannot_load_missions"))
                    : undefined
            }
            isContentContainerFlex={!missionsLoading && !missionsError && !items.length}
            virtualizedItems={showList ? items : undefined}
            renderItem={showList ? renderRow : undefined}
        >
            {showList ? undefined : missionsLoading ? (
                <Loading />
            ) : missionsError ? (
                <EmptyScreen
                    image={NoActivitiesImage}
                    text={i18n.t("screens.user.missions.cannot_load_missions")}
                />
            ) : !items.length ? (
                <EmptyScreen
                    image={NoActivitiesImage}
                    text={i18n.t("screens.user.colleague_details.no_contracts")}
                />
            ) : null}
        </ScreenCard>
    );
};

export default ColleagueContractsScreen;
